import { useMutation } from '@apollo/client';

import {
  CompanyCreationDocument,
  CompanyCreationDocumentType,
} from '__generated__/GQL';
import asBentoPage from 'common/bento/hoc/asBentoPage';
import Button from 'components/_core/Button';
import Text from 'components/_core/Text';
import Header from 'features/Onboarding/components/Header';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';
import OBStickyButton from 'features/Onboarding/components/OBStickyButton/OBStickyButton';
import DisplayOrUploadDoc from 'features/Onboarding/legacy/components/DisplayOrUploadDoc';
import { UploadDocFromTemplatePageProps } from 'features/Onboarding/legacy/components/UploadDocFromTemplatePage/types';
import { Subtitle } from 'features/Onboarding/legacy/Pages/styles';
import {
  DownloadableNonGenericDocumentLocales,
  NonDownloadableNonGenericDocumentLocales,
} from 'features/Onboarding/shineStartTypes';
import { getUploadKybFunction } from 'features/Storage/upload';

import getDocumentLocales from '../../../ShineStartVerification/libs/getDocumentLocales';
import getShineStartDownloadFormUrl from '../../../ShineStartVerification/libs/getDownloadFormUrl';
import locales from '../../../ShineStartVerification/pages/DocumentUpload/locales';
import {
  LinkWrapper,
  UploadSectionWrapper,
} from '../../../ShineStartVerification/pages/DocumentUpload/styles';
import { DeleteCompanyCreationDocumentDocument } from '../../graphql/mutation/delete-company-creation-document.gql';
import { ProvideCompanyCreationDocumentDocument } from '../../graphql/mutation/provide-company-creation-document.gql';
import { CompanyCreationDocumentsModuleDataDocument } from '../../graphql/queries/companyCreationDocumentsModuleData.gql';

function isDownloadableDocumentLocales(
  variableLocales?: UploadDocFromTemplatePageProps['locales'],
): variableLocales is DownloadableNonGenericDocumentLocales {
  if (
    (variableLocales as DownloadableNonGenericDocumentLocales).downloadFormLink
  ) {
    return true;
  }
  return false;
}

function localesHaveUploadDescription(
  variableLocales?: UploadDocFromTemplatePageProps['locales'],
): variableLocales is NonDownloadableNonGenericDocumentLocales {
  if (
    (variableLocales as NonDownloadableNonGenericDocumentLocales)
      .uploadDescription
  ) {
    return true;
  }
  return false;
}

const SASU_SPECIFIC_DOCUMENTS: Partial<
  Record<CompanyCreationDocumentType, string>
> = {
  [CompanyCreationDocumentType.NoCriminalConvictionStatement]:
    'https://storage.googleapis.com/shine-public/product/sasu/templates/declaration-de-non-condamnation.pdf',
  [CompanyCreationDocumentType.PowerOfAttorney]:
    'https://storage.googleapis.com/shine-public/product/sasu/templates/pouvoir-du-mandataire.pdf',
};

const getDownloadFormUrl = (document: CompanyCreationDocument) => {
  const { type } = document;

  if (type in SASU_SPECIFIC_DOCUMENTS) {
    return SASU_SPECIFIC_DOCUMENTS[type];
  }

  return getShineStartDownloadFormUrl(document);
};

interface DocumentUploadProps {
  onNext: () => void;
  document: CompanyCreationDocument;
}
const DocumentUpload = asBentoPage<DocumentUploadProps>(
  ({ document, Layout, onNext, onPrev }) => {
    const [addDocument, { loading: isAddingDocument }] = useMutation(
      ProvideCompanyCreationDocumentDocument,
      {
        awaitRefetchQueries: true,
        refetchQueries: [CompanyCreationDocumentsModuleDataDocument],
      },
    );
    const [deleteDocument, { loading: isDeletingDocument }] = useMutation(
      DeleteCompanyCreationDocumentDocument,
      {
        awaitRefetchQueries: true,
        refetchQueries: [CompanyCreationDocumentsModuleDataDocument],
      },
    );

    const variableLocales = getDocumentLocales(document);
    const description =
      'documentDescription' in variableLocales
        ? variableLocales.documentDescription
        : variableLocales.completeDocumentDescription;
    const downloadFormUrl = getDownloadFormUrl(document);
    const uploadFormSubtitle = !isDownloadableDocumentLocales(variableLocales)
      ? locales.uploadFormSubtitle
      : locales.formatString(locales.uploadFormSubtitleWrapper, {
          subtitle: locales.uploadFormSubtitle,
        });
    const isCtaDisabled =
      !document.file || isAddingDocument || isDeletingDocument;

    return (
      <Layout nav={<OBNavigation onPrev={onPrev} />}>
        <Header
          description={description}
          title={variableLocales.documentName}
        />
        {isDownloadableDocumentLocales(variableLocales) ? (
          <>
            <Subtitle>
              {locales.formatString(locales.downloadFormSubtitleWrapper, {
                subtitle: variableLocales.downloadFormSubtitle,
              })}
            </Subtitle>
            <Text>{variableLocales.downloadFormDescription}</Text>
            <LinkWrapper>
              <Button
                as="a"
                href={downloadFormUrl}
                icon="download"
                iconPosition="left"
                target="_blank"
                variant="inline-primary"
              >
                {variableLocales.downloadFormLink}
              </Button>
            </LinkWrapper>
          </>
        ) : null}
        <Subtitle>{uploadFormSubtitle}</Subtitle>
        {localesHaveUploadDescription(variableLocales) ? (
          <Text>{variableLocales.uploadDescription}</Text>
        ) : null}
        <UploadSectionWrapper>
          <DisplayOrUploadDoc
            existingDocumentUrl={document.file?.url}
            isDeletingExistingDocument={isDeletingDocument}
            locales={{
              uploadedDocumentName: variableLocales.uploadedDocumentName,
              uploadFailureMessage: locales.uploadFailureMessage,
              uploadFormBrowse: locales.uploadFormBrowse,
              uploadFormDragDrop: variableLocales.uploadFormDragDrop,
            }}
            onDeleteExistingDocument={async () => {
              await deleteDocument({
                variables: {
                  input: {
                    applicationId: document.applicationId,
                    documentId: document.id,
                  },
                },
              });
            }}
            onSuccessfulUpload={async ({ bucketName, fileRef }) => {
              await addDocument({
                variables: {
                  input: {
                    applicationId: document.applicationId,
                    fileBucketName: bucketName,
                    filePath: fileRef,
                    type: document.type,
                  },
                },
              });
            }}
            uploadFile={getUploadKybFunction(document.applicationId)}
          />
        </UploadSectionWrapper>

        <OBStickyButton isDisabled={isCtaDisabled} onClick={onNext}>
          {locales.ctaContinue}
        </OBStickyButton>
      </Layout>
    );
  },
);

export default DocumentUpload;
